import {
    IonCard,
    IonCardContent,
    IonCardTitle,
    IonCol,
    IonContent,
    IonIcon,
    IonPage,
    IonRow,
} from "@ionic/react";
import Header from "../../../../components/Headers/Header";
import PageTitle from "../../../../components/PageTitle/PageTitle";
import React, { useContext, useState } from "react";
import ErrorAlert from "../../../../components/Errors/ErrorAlert";
import { PersonContext } from "../../../../context/PersonContext";
import SelectFromAllBarns from "../../../../components/Barn/SelectFromAllBarns";
import { Barn, Horse, Owner, Rider, Trainer } from "../../../../models";
import Spinner from "../../../../components/Spinners/Spinner";
import { getHorsesByBarnId } from "../../../../utilities/horse/Horse";
import { getRidersByBarnId } from "../../../../utilities/rider/Rider";
import UpdateBarnForm from "../../../../components/Barn/UpdateBarnForm";
import { chevronDown, chevronUp } from "ionicons/icons";
import AdminMergeHorsesTable from "../../../../components/Horse/AdminMergeHorsesTable";

interface FormattedHorse {
    id: string
    horse: Horse
    isSelected: boolean
}

const BarnMergeElementsPage: React.FC = () => {
    const user = useContext(PersonContext);

    // Selected Barn Details
    const [selectedBarn, setSelectedBarn] = useState<Barn | null | undefined>();
    const [isBarnCardExpanded, setIsBarnCardExpanded] = useState(true);

    // Horse Details
    const [horses, setHorses] = useState<Horse[] | null | undefined>();
    const [formattedHorses, setFormattedHorses] = useState<FormattedHorse[] | null | undefined>();
    const [isLoadingHorses, setIsLoadingHorses] = useState(false);
    const [showHorseModal, setShowHorseModal] = useState(false);
    const [onlyShowSelectedHorses, setOnlyShowSelectedHorses] = useState(false);
    const [horseError, setHorseError] = useState("");

    // Rider Details
    const [riders, setRiders] = useState<Rider[] | null | undefined>();
    const [isLoadingRiders, setIsLoadingRiders] = useState(false);
    const [riderError, setRiderError] = useState("");

    // Owner Details
    const [owners, setOwners] = useState<Owner[] | null | undefined>();
    const [isLoadingOwners, setIsLoadingOwners] = useState(false);
    const [ownerError, setOwnerError] = useState("");

    // Trainer Details
    const [trainers, setTrainers] = useState<Trainer[] | null | undefined>();
    const [isLoadingTrainers, setIsLoadingTrainers] = useState(false);
    const [trainerError, setTrainerError] = useState("");

    // Basic state
    const [isLoading, setIsLoading] = useState(false);
    const [progressMessage, setProgressMessage] = useState("");
    const [error, setError] = useState("");

    const getBarnRiders = async (barn: Barn) => {
        setIsLoadingRiders(true);
        const queryResult = await getRidersByBarnId(barn.id);
        if (queryResult.isSuccess) {
            const barnRiders: Rider[] = queryResult.result;
            setRiders(barnRiders);
        } else {
            setRiderError("Could not load the barn riders.");
        }
        setIsLoadingRiders(false);
    }

    const handleSelectedBarn = async (barn: Barn) => {
        setIsLoading(true);
        setSelectedBarn(barn);
        
        await getBarnRiders(barn);
        setIsLoading(false);
    }

    return (
        <IonPage className="bg-light">
            <Header />
            <IonContent>
                <PageTitle title="Barn Merge Elements" />
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <IonCard mode="md" className="ion-padding bg-white">
                            <IonCardTitle>Step 1: Select a Barn</IonCardTitle>
                            <IonCardContent>
                                {error && <ErrorAlert error={error} width="12" />}
                                <IonRow className="ion-justify-content-center">
                                    <IonCol size="12">
                                        <SelectFromAllBarns onSelect={(barn: Barn) => handleSelectedBarn(barn)} />
                                    </IonCol>
                                </IonRow>
                            </IonCardContent>
                        </IonCard>
                    </IonCol>
                </IonRow>

                {/* Barn Details Card */}
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <IonCard mode="md" className="ion-padding bg-white">
                            <IonCardTitle>
                                <IonRow>
                                    <IonCol size="8">
                                        Barn Details
                                    </IonCol>
                                    <IonCol size="4" className="ion-text-right" onClick={() => setIsBarnCardExpanded(!isBarnCardExpanded)}>
                                        <IonIcon icon={isBarnCardExpanded ? chevronDown : chevronUp} />
                                    </IonCol>
                                </IonRow>
                            </IonCardTitle>
                            {isBarnCardExpanded && (
                                <IonCardContent>
                                    {isLoading ?
                                        <IonRow className="ion-justify-content-center">
                                            <IonCol size="12" className="ion-text-center">
                                                <Spinner />
                                            </IonCol>
                                        </IonRow>
                                        :
                                        <>
                                            {error && <ErrorAlert error={error} width="12" />}
                                            <IonRow className="ion-justify-content-center">
                                                <IonCol size="12">
                                                    {selectedBarn ? 
                                                        <UpdateBarnForm currentBarn={selectedBarn} onChange={(updatedBarn: Barn) => setSelectedBarn(updatedBarn)} />
                                                        :
                                                        <p>No barn currently selected.</p>
                                                    }
                                                </IonCol>
                                            </IonRow>
                                        </>
                                    }
                                </IonCardContent>
                            )}
                        </IonCard>
                    </IonCol>
                </IonRow>

                {/* Horse Card */}
                <AdminMergeHorsesTable barn={selectedBarn} />

                {/* Rider Card */}
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <IonCard mode="md" className="ion-padding bg-white">
                            <IonCardTitle>Riders</IonCardTitle>
                            {isLoadingRiders ?
                                <IonRow className="ion-justify-content-center">
                                    <IonCol size="12" className="ion-text-center">
                                        <Spinner />
                                    </IonCol>
                                </IonRow>
                                :
                                <>
                                    <IonCardContent>
                                        {riderError && <ErrorAlert error={riderError} width="12" />}
                                        <IonRow className="ion-justify-content-center">
                                            <IonCol size="12">
                                                
                                            </IonCol>
                                        </IonRow>
                                    </IonCardContent>
                                </>
                            }
                        </IonCard>
                    </IonCol>
                </IonRow>
            </IonContent>
        </IonPage>
    );
};

export default BarnMergeElementsPage;